/* eslint-disable max-len */
import React, { useContext, useState } from 'react';
import useFeatures from '../../hooks/useFeatures';
import Button from '../../atoms/Button/Button';
import ExternalLinkIcon from '../../atoms/ExternalLinkIcon/ExternalLinkIcon';
import { AirDropContext } from '../../../utils/AirDropProvider';
import './SideBanner.scss';
import useTranslation from '../../hooks/useTranslation';

interface SideBannerProps {
  leftSideImage?: string;
  heading?: string;
  textField?: string;
  page?: string;
  customClass?: string;
  imagePlaceholderClassName?: string
}

function SideBanner({
  leftSideImage = '',
  heading = '',
  textField = '',
  page = '',
  customClass = '',
  imagePlaceholderClassName = '',
}: SideBannerProps) {
  const config: any = useContext(AirDropContext);
  const { isChannels, isTurbo, isPDotCom } = useFeatures();
  const { translateText } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);

  const isAbsoluteUrl = (url: string = '') => url.indexOf('http://') === 0 || url.indexOf('https://') === 0;

  const getImageUrl = (pg: string) => {
    const url = config?.leftSideImages && config?.leftSideImages[pg];
    if (url && isAbsoluteUrl(url)) {
      return url;
    }
    return null;
  };

  const learnMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const url = config.pPlusLearnMoreUrl ?? 'https://www.pearson.com/en-us/pearsonplus.html';
    window.open(url);
  };

  let bgClass;
  switch (page) {
    case 'signin':
      bgClass = 'side-banner__bgSignIn';
      break;
    case 'signin-pplus':
      bgClass = 'side-banner__bgSignInPPlus';
      break;
    case 'signup':
      bgClass = 'side-banner__bgCreateAcc';
      break;
    case 'forgot-unpw':
      bgClass = 'side-banner__bgForgotUnPw';
      break;
    case 'reset-pw':
      bgClass = 'side-banner__bgResetPw';
      break;
    case 'consents':
      bgClass = 'side-banner__bgConsents';
      break;
    default:
      bgClass = 'side-banner__bgSignIn';
      break;
  }
  // override the background image if absolute url is found in the airdrop for this page
  const bgStyle = (isPDotCom && getImageUrl(page)) ? { backgroundImage: `url('${getImageUrl(page)}')` } : {};

  const onImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div className={`side-banner ${bgClass}`} style={bgStyle}>
      <div className="side-banner__top">
        <div className="side-banner__heading">
          {heading}
        </div>
        {isTurbo && page === 'signin-pplus' ? (
          <p className="side-banner__subheading">
            {textField}
          </p>
        ) : (
          <p className="side-banner__text">
            {textField}
          </p>
        )}
        {isChannels && (page === 'signin' || page === 'signup') && (
          <div className="side-banner__bullets">
            <ul>
              <li>{translateText('Learn the toughest concepts covered in your class with step-by-step video tutorials.')}</li>
              <li>{translateText('Become exam ready with thousands of exam-like practice questions and step-by-step video solutions.')}</li>
              <li>{translateText('Spend less time studying - focus your study time with our content recommendations based on your textbook.')}</li>
            </ul>
          </div>
        )}
        {isTurbo && page === 'signin-pplus' && (
          <>
            <p className="side-banner__text-strong">
              {translateText('Still need to make a purchase? Find what you need in Pearson+')}
            </p>
            <div className="side-banner__bullets-tight">
              <ul>
                <li>{translateText('eTextbooks with built-in tools that simplify studying')}</li>
                <li>{translateText('Study prep to get you exam-ready, with video lessons, practice problems, and more')}</li>
                <li>{translateText('Access it all in the Pearson+ app to learn anytime, anywhere')}</li>
              </ul>
            </div>
            <Button className="side-banner__button" type="button" onClick={learnMoreClick}>
              <span>{translateText('Learn more')}</span>
              <svg
                role="img"
                focusable="false"
                aria-labelledby="svg-title-external-link"
              >
                <title id="svg-title-external-link">{translateText('opens in a new tab or window')}</title>
                <ExternalLinkIcon />
              </svg>
            </Button>
          </>
        )}
      </div>
      <div className="side-banner__bottom">
        {(isChannels || isTurbo) && (
          <img className={`side-banner__image ${customClass} ${!imageLoaded ? imagePlaceholderClassName : ''}`} src={leftSideImage} alt="" onLoad={onImageLoad} />
        )}
      </div>
    </div>
  );
}

export default SideBanner;
